/**
 * SlideShape — Draggable, resizable, rotatable shape on the slide canvas
 * Supports: text, rect, ellipse, image, line
 * Rotation handle: small circle above shape, drag to rotate
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const MIN_SIZE = 20;

export default function SlideShape({ shape, selected, scale, onSelect, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [imgUrl, setImgUrl] = useState(null);
  const shapeRef = useRef(null);
  const textRef = useRef(null);
  const dragState = useRef(null);
  const resizeState = useRef(null);
  const rotateState = useRef(null);

  // Load image URL from storageKey
  useEffect(() => {
    if (shape.type === 'image' && shape.storageKey) {
      fetch(`/api/work/documents/image-url?key=${encodeURIComponent(shape.storageKey)}`, { credentials: 'include' })
        .then(r => r.json()).then(d => setImgUrl(d.url)).catch(() => {});
    }
  }, [shape.storageKey, shape.type]);

  // ── Drag to move ──────────────────────────────────────────────
  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.dataset.resize || e.target.dataset.rotate) return;
    e.stopPropagation();
    onSelect(shape.id, e.shiftKey || e.ctrlKey || e.metaKey);
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = shape.x;
    const origY = shape.y;
    dragState.current = { startX, startY, origX, origY };

    const onMove = (me) => {
      if (!dragState.current) return;
      const dx = (me.clientX - dragState.current.startX) / scale;
      const dy = (me.clientY - dragState.current.startY) / scale;
      onUpdate({ ...shape, x: dragState.current.origX + dx, y: dragState.current.origY + dy });
    };
    const onUp = () => {
      dragState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [shape, scale, onSelect, onUpdate]);

  // ── Resize handle ─────────────────────────────────────────────
  const handleResizeMouseDown = useCallback((e, corner) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { x: shape.x, y: shape.y, w: shape.width, h: shape.height };
    resizeState.current = { startX, startY, orig, corner };

    const onMove = (me) => {
      if (!resizeState.current) return;
      const dx = (me.clientX - resizeState.current.startX) / scale;
      const dy = (me.clientY - resizeState.current.startY) / scale;
      const { orig: o, corner: c } = resizeState.current;
      let nx = o.x, ny = o.y, nw = o.w, nh = o.h;
      if (c.includes('e')) nw = Math.max(MIN_SIZE, o.w + dx);
      if (c.includes('s')) nh = Math.max(MIN_SIZE, o.h + dy);
      if (c.includes('w')) { nw = Math.max(MIN_SIZE, o.w - dx); nx = o.x + o.w - nw; }
      if (c.includes('n')) { nh = Math.max(MIN_SIZE, o.h - dy); ny = o.y + o.h - nh; }
      onUpdate({ ...shape, x: nx, y: ny, width: nw, height: nh });
    };
    const onUp = () => {
      resizeState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [shape, scale, onUpdate]);

  // ── Rotation handle ───────────────────────────────────────────
  const handleRotateMouseDown = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = shapeRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    rotateState.current = { cx, cy };

    const onMove = (me) => {
      if (!rotateState.current) return;
      const { cx, cy } = rotateState.current;
      const angle = Math.atan2(me.clientY - cy, me.clientX - cx) * (180 / Math.PI) + 90;
      const normalized = ((angle % 360) + 360) % 360;
      onUpdate({ ...shape, rotation: Math.round(normalized) });
    };
    const onUp = () => {
      rotateState.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [shape, onUpdate]);

  // ── Double-click to edit text ─────────────────────────────────
  const handleDoubleClick = (e) => {
    e.stopPropagation();
    if (shape.type !== 'image' && shape.type !== 'line') setEditing(true);
  };

  useEffect(() => {
    if (editing && textRef.current) textRef.current.focus();
  }, [editing]);

  const rotation = shape.rotation || 0;

  // ── Resize handles ────────────────────────────────────────────
  const resizeHandles = selected && !editing ? [
    { corner: 'nw', style: { top: -5, left: -5, cursor: 'nw-resize' } },
    { corner: 'ne', style: { top: -5, right: -5, cursor: 'ne-resize' } },
    { corner: 'se', style: { bottom: -5, right: -5, cursor: 'se-resize' } },
    { corner: 'sw', style: { bottom: -5, left: -5, cursor: 'sw-resize' } },
    { corner: 'n',  style: { top: -5, left: '50%', transform: 'translateX(-50%)', cursor: 'n-resize' } },
    { corner: 's',  style: { bottom: -5, left: '50%', transform: 'translateX(-50%)', cursor: 's-resize' } },
    { corner: 'e',  style: { right: -5, top: '50%', transform: 'translateY(-50%)', cursor: 'e-resize' } },
    { corner: 'w',  style: { left: -5, top: '50%', transform: 'translateY(-50%)', cursor: 'w-resize' } },
  ] : [];

  // ── Shape content ─────────────────────────────────────────────
  const renderContent = () => {
    if (shape.type === 'image') {
      return imgUrl
        ? <img src={imgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: shape.radius || 0, display: 'block', pointerEvents: 'none', userSelect: 'none' }} />
        : <div style={{ width: '100%', height: '100%', background: '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#9ca3af' }}>Loading…</div>;
    }
    if (shape.type === 'line') {
      return (
        <svg width={shape.width} height={shape.height} style={{ display: 'block', overflow: 'visible' }}>
          <line x1={0} y1={shape.height / 2} x2={shape.width} y2={shape.height / 2}
            stroke={shape.stroke || '#333'} strokeWidth={shape.strokeWidth || 2} />
        </svg>
      );
    }
    if (editing) {
      return (
        <textarea
          ref={textRef}
          defaultValue={shape.text || ''}
          onBlur={(e) => { onUpdate({ ...shape, text: e.target.value }); setEditing(false); }}
          style={{
            width: '100%', height: '100%', background: 'transparent', border: 'none', outline: 'none',
            resize: 'none', padding: '4px', boxSizing: 'border-box',
            fontSize: shape.fontSize || 16, fontWeight: shape.fontWeight || 'normal',
            color: shape.textColor || '#333', textAlign: shape.textAlign || 'center',
            fontFamily: 'inherit',
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: shape.textAlign === 'left' ? 'flex-start' : shape.textAlign === 'right' ? 'flex-end' : 'center',
        padding: '4px', boxSizing: 'border-box', overflow: 'hidden',
        fontSize: shape.fontSize || 16, fontWeight: shape.fontWeight || 'normal',
        color: shape.textColor || '#333', textAlign: shape.textAlign || 'center',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', pointerEvents: 'none', userSelect: 'none',
      }}>
        {shape.text || ''}
      </div>
    );
  };

  // ── Shape background/border style ─────────────────────────────
  const shapeStyle = {
    position: 'absolute',
    left: shape.x,
    top: shape.y,
    width: shape.width,
    height: shape.height,
    zIndex: shape.zIndex || 1,
    opacity: shape.opacity ?? 1,
    transform: `rotate(${rotation}deg)`,
    transformOrigin: 'center center',
    cursor: 'move',
    userSelect: 'none',
    boxSizing: 'border-box',
    ...(shape.type === 'ellipse'
      ? { borderRadius: '50%', background: shape.fill || '#a5b4fc', border: shape.stroke ? `${shape.strokeWidth || 1}px solid ${shape.stroke}` : 'none' }
      : shape.type === 'rect' || shape.type === 'text'
      ? { borderRadius: shape.radius || 0, background: shape.fill || (shape.type === 'text' ? 'transparent' : '#a5b4fc'), border: shape.stroke ? `${shape.strokeWidth || 1}px solid ${shape.stroke}` : 'none' }
      : shape.type === 'image'
      ? { background: 'transparent', border: selected ? '2px solid #6366f1' : 'none' }
      : { background: 'transparent' }
    ),
    outline: selected && shape.type !== 'image' ? '2px solid #6366f1' : 'none',
    outlineOffset: 1,
  };

  return (
    <div
      ref={shapeRef}
      style={shapeStyle}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
    >
      {renderContent()}

      {/* Rotation handle — shown when selected */}
      {selected && !editing && (
        <div
          data-rotate="true"
          onMouseDown={handleRotateMouseDown}
          title={`Rotate (${rotation}°)`}
          style={{
            position: 'absolute',
            top: -28,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#6366f1',
            border: '2px solid white',
            cursor: 'grab',
            zIndex: 100,
            boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
          }}
        />
      )}

      {/* Resize handles */}
      {resizeHandles.map(({ corner, style }) => (
        <div
          key={corner}
          data-resize={corner}
          onMouseDown={(e) => handleResizeMouseDown(e, corner)}
          style={{
            position: 'absolute',
            width: 10, height: 10,
            background: 'white',
            border: '2px solid #6366f1',
            borderRadius: 2,
            zIndex: 100,
            ...style,
          }}
        />
      ))}
    </div>
  );
}
